import * as React from "react";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";
import { StatusBadge } from "./status-badge";
import { Button } from "./button";
import { Switch } from "./switch";
import { 
  MapPin, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Loader2,
  Phone,
  Truck,
  AlertTriangle
} from "lucide-react";
import { EnhancedTransporter } from "../../lib/api";

interface MobileTransporterCardProps {
  transporter: EnhancedTransporter;
  onView: (id: string) => void;
  onEdit: (transporter: EnhancedTransporter) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onVerify: (id: string) => void;
  isDeleting?: boolean;
  isToggling?: boolean;
  className?: string;
}

const MobileTransporterCard = React.forwardRef<
  HTMLDivElement,
  MobileTransporterCardProps
>(({ 
  transporter, 
  onView, 
  onEdit, 
  onDelete, 
  onToggleActive, 
  onVerify,
  isDeleting = false, 
  isToggling = false,
  className,
  ...props 
}, ref) => {
  
  // Helper function to check if transporter is verified
  const isVerified = (transporter: EnhancedTransporter) => {
    return transporter.verificationStatus === 'verified';
  };

  return (
    <Card
      ref={ref}
      className={cn(
        "mobile-transporter-card bg-card border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-all hover:border-primary/20 cursor-pointer",
        className
      )}
      onClick={() => onView(transporter._id)}
      {...props}
    >
      <CardContent className="p-0 space-y-4">
        {/* Header with name and verification status */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate">{transporter.name}</h3>
            <p className="text-sm text-muted-foreground truncate">{transporter.email}</p>
          </div>
          <div className="flex flex-col items-end space-y-2 ml-2">
            <StatusBadge status={transporter.verificationStatus} />
          </div>
        </div>

        {/* Contact and location information */}
        <div className="space-y-2">
          <div className="flex items-center text-sm text-muted-foreground">
            <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{transporter.mobile}</span>
          </div>
          {transporter.alternatePhone && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{transporter.alternatePhone}</span>
            </div>
          )}
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {transporter.address?.city}, {transporter.address?.state}
            </span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <Truck className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>
              {transporter.vehicles?.length || 0} vehicles • 
              {transporter.completedTrips || 0} trips • 
              Rating: {transporter.rating || 'N/A'}
            </span>
          </div>
        </div>

        {/* Status and actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center space-x-3">
            <StatusBadge status={transporter.isActive ? 'active' : 'inactive'} />
            <div className="flex items-center space-x-2">
              <Switch
                checked={transporter.isActive}
                onCheckedChange={(checked) => {
                  onToggleActive(transporter._id);
                }}
                disabled={isToggling}
                aria-label={`Toggle ${transporter.name} active status`}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-xs text-muted-foreground">
                {transporter.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          
          <div className="flex space-x-1">
            {transporter.verificationStatus === 'pending' && (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onVerify(transporter._id);
                }}
                aria-label={`Verify ${transporter.name}`}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(transporter);
              }}
              aria-label={`Edit ${transporter.name}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(transporter._id);
              }}
              disabled={isDeleting}
              aria-label={`Delete ${transporter.name}`}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 text-red-500" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

MobileTransporterCard.displayName = "MobileTransporterCard";

export { MobileTransporterCard };